import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TimezoneSelect, { browserTimezone, fmtSlotInTz } from "../components/TimezoneSelect";
import { DateInput } from "../components/DateInput";
import { futureTimeSlots } from "../utils/timeSlots";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:30801");


async function fetchOffer(token: string) {
  const r = await fetch(`${API_BASE}/api/public/interview-slots/interviewer/${token}`);
  return r.json();
}

async function submitSlots(token: string, slots: { slot_1: string; slot_2?: string; slot_3?: string }) {
  const r = await fetch(`${API_BASE}/api/public/interview-slots/interviewer/${token}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(slots),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to submit");
  }
  return r.json();
}

const todayStr = new Date().toISOString().split("T")[0];

function slotToISO(date: string, time: string): string {
  return date && time ? `${date}T${time}` : "";
}

export default function InterviewAvailabilityPage() {
  const { token } = useParams<{ token: string }>();
  const [slots, setSlots] = useState({
    slot_1_date: "", slot_1_time: "09:00",
    slot_2_date: "", slot_2_time: "09:00",
    slot_3_date: "", slot_3_time: "09:00",
  });
  const [tz, setTz] = useState(browserTimezone());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const { data: rawData, isLoading } = useQuery({
    queryKey: ["interviewerOffer", token],
    queryFn: () => fetchOffer(token!),
    enabled: !!token,
  });
  const data = rawData as any;

  const s1 = slotToISO(slots.slot_1_date, slots.slot_1_time);
  const s2 = slotToISO(slots.slot_2_date, slots.slot_2_time);
  const s3 = slotToISO(slots.slot_3_date, slots.slot_3_time);

  async function handleSubmit() {
    if (!s1) { setError("Please add at least one slot."); return; }
    setSubmitting(true);
    setError("");
    try {
      await submitSlots(token!, {
        slot_1: new Date(s1).toISOString(),
        slot_2: s2 ? new Date(s2).toISOString() : undefined,
        slot_3: s3 ? new Date(s3).toISOString() : undefined,
      });
      setDone(true);
    } catch (e: any) {
      setError(e.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  const card: React.CSSProperties = {
    background: "white", borderRadius: 16, padding: 32,
    boxShadow: "0 4px 32px rgba(0,0,0,0.10)", maxWidth: 500, width: "100%", margin: "0 auto",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0",
    borderRadius: 8, fontSize: 14, boxSizing: "border-box",
    color: "#0f172a",
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 900, color: "#fff" }}>HR</span>
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>HRxAI</span>
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>Interview Scheduling</div>
      </div>

      {isLoading && (
        <div style={card}>
          <div style={{ textAlign: "center", padding: "32px 0", color: "#64748b" }}>Loading…</div>
        </div>
      )}

      {!isLoading && (data?.status === "expired" || data?.status === "confirmed") && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>{data.status === "confirmed" ? "✅" : "⏰"}</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>
              {data.status === "confirmed" ? "Interview Confirmed" : "Link Expired"}
            </h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
              {data.status === "confirmed"
                ? "The candidate has already confirmed their slot. Check your email for the calendar invite."
                : "This availability request has expired. Please ask the recruiter to send a new one."}
            </p>
          </div>
        </div>
      )}

      {done && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 22 }}>Slots Submitted!</h2>
            <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>
              The candidate will receive an email with your available slots and can pick one. You'll get a calendar invite as soon as they confirm.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[s1, s2, s3].filter(Boolean).map((s, i) => (
                <div key={i} style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#15803d", fontWeight: 600 }}>
                  Option {i + 1}: {fmtSlotInTz(new Date(s).toISOString(), tz)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isLoading && data?.status === "awaiting_interviewer" && !done && (
        <div style={card}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ margin: "0 0 4px", color: "#1e3a8a", fontSize: 20 }}>Share Your Availability</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 13, lineHeight: 1.6 }}>
              Hi <strong>{data.interviewer_name || "there"}</strong> — you've been asked to interview{" "}
              <strong>{data.candidate_name}</strong> for the <strong>{data.job_title}</strong> role.
              Please add up to 3 time slots when you're free.
            </p>
          </div>

          {/* Details row */}
          <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
            {data.interview_type && (
              <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                {data.interview_type.replace("_", " ")}
              </span>
            )}
            {data.duration_minutes && (
              <span style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                {data.duration_minutes} min
              </span>
            )}
          </div>

          {/* Timezone selector */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 6 }}>YOUR TIMEZONE</div>
            <TimezoneSelect value={tz} onChange={setTz} style={{ width: "100%", boxSizing: "border-box" as const }} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Times you enter below will be interpreted in this timezone</div>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
            {([1, 2, 3] as const).map(i => {
              const dateKey = `slot_${i}_date` as keyof typeof slots;
              const timeKey = `slot_${i}_time` as keyof typeof slots;
              return (
                <div key={i}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
                    Slot {i} {i === 1 ? "*" : <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional)</span>}
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <DateInput value={slots[dateKey]} min={todayStr}
                      onChange={e => setSlots(s => ({ ...s, [dateKey]: e.target.value }))}
                      style={{ ...inputStyle }} />
                    <select value={slots[timeKey]}
                      onChange={e => setSlots(s => ({ ...s, [timeKey]: e.target.value }))}
                      disabled={!slots[dateKey]}
                      style={{ ...inputStyle, background: slots[dateKey] ? "white" : "#f8fafc", cursor: slots[dateKey] ? "pointer" : "not-allowed" }}>
                      {futureTimeSlots(slots[dateKey], todayStr).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>

          <p style={{ margin: "0 0 20px", fontSize: 11, color: "#94a3b8" }}>
            Slots are interpreted in your selected timezone above. The candidate will see them in the same timezone.
          </p>

          <button
            onClick={handleSubmit}
            disabled={submitting || !s1}
            style={{
              width: "100%", padding: "14px 0", borderRadius: 10, border: "none",
              background: (!s1 || submitting) ? "#94a3b8" : "linear-gradient(135deg, #1e3a8a, #0ea5e9)",
              color: "white", fontWeight: 700, fontSize: 16,
              cursor: (!s1 || submitting) ? "not-allowed" : "pointer",
            }}>
            {submitting ? "Submitting…" : "Submit My Available Slots →"}
          </button>
        </div>
      )}
    </div>
  );
}
